import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Download, RefreshCw, CheckCircle, XCircle, AlertCircle, Send, Shuffle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_STYLES = {
  assigned:  { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Assigned' },
  submitted: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Submitted' },
  redeemed:  { bg: 'bg-green-100', text: 'text-green-700', label: 'Redeemed' },
  expired:   { bg: 'bg-red-100',   text: 'text-red-600',   label: 'Expired' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.assigned;
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

export default function AdminCoupons() {
  const { user } = useAuth();
  const [data, setData] = useState({ coupons: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState(null); // {coupon, type: 'approve'|'reject'}
  const [actionNotes, setActionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Send Custom Coupon modal state
  const [sendModal, setSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ partner_email: '', custom_code: '', offer_id: '' });
  const [sendSubmitting, setSendSubmitting] = useState(false);
  const [activeOffers, setActiveOffers] = useState([]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/admin/coupons${params}`);
      setData(res.data || { coupons: [], stats: {} });
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchActiveOffers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/partner-offers`);
      setActiveOffers((res.data || []).filter(o => o.is_active));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const openSendModal = () => {
    fetchActiveOffers();
    setSendForm({ partner_email: '', custom_code: '', offer_id: '' });
    setSendModal(true);
  };

  const autoGenerateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const code = Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('') +
      '-' + Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setSendForm(f => ({ ...f, custom_code: code }));
  };

  const handleSendCustomCoupon = async () => {
    if (!sendForm.partner_email.trim()) { toast.error('Partner email is required'); return; }
    if (!sendForm.custom_code.trim()) { toast.error('Coupon code is required'); return; }
    if (!sendForm.offer_id) { toast.error('Please select an offer'); return; }
    setSendSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/coupons/send-custom`, {
        partner_email: sendForm.partner_email.trim(),
        custom_code: sendForm.custom_code.trim().toUpperCase(),
        offer_id: sendForm.offer_id,
      });
      toast.success(res.data.message || 'Coupon sent successfully!');
      setSendModal(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send coupon');
    } finally {
      setSendSubmitting(false);
    }
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    setSubmitting(true);
    const { coupon, type } = actionDialog;
    try {
      const url = `${API}/admin/coupons/${coupon.id}/${type}`;
      await axios.post(url, { notes: actionNotes });
      toast.success(type === 'approve' ? `Coupon approved — ₹5,100 credited!` : 'Coupon rejected');
      setActionDialog(null);
      setActionNotes('');
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const exportCSV = () => {
    const coupons = data.coupons;
    if (!coupons.length) { toast.info('No data to export'); return; }
    const headers = ['Partner Name', 'Email', 'Assigned Code', 'Submitted Code', 'Status', 'Assigned Date', 'Submitted Date'];
    const rows = coupons.map(c => [
      `"${c.partner_name || ''}"`, `"${c.partner_email || ''}"`,
      c.coupon_code || '', c.submitted_code || '',
      c.status || '', fmtDate(c.assigned_at), fmtDate(c.submitted_at),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'diwali-coupons.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const stats = data.stats || {};
  const coupons = data.coupons || [];

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Diwali Coupons</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage partner Diwali coupon redemptions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={openSendModal}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-custom-coupon-btn"
            >
              <Send className="h-4 w-4 mr-1.5" /> Send Custom Coupon
            </Button>
            <Button variant="outline" size="sm" onClick={fetchCoupons} disabled={loading} data-testid="refresh-coupons">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-coupons-csv">
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="coupon-stats">
          {[
            { label: 'Total Assigned', value: stats.total ?? 0, color: 'text-slate-700', bg: 'bg-slate-50' },
            { label: 'Submitted', value: stats.submitted ?? 0, color: 'text-amber-700', bg: 'bg-amber-50' },
            {
              label: 'Redeemed',
              value: stats.max_redemptions === null || stats.max_redemptions === undefined
                ? `${stats.redeemed ?? 0} / ∞`
                : `${stats.redeemed ?? 0} / ${stats.max_redemptions}`,
              color: 'text-green-700', bg: 'bg-green-50'
            },
            {
              label: 'Slots Left',
              value: stats.remaining_slots === null || stats.remaining_slots === undefined
                ? '∞'
                : stats.remaining_slots,
              color: 'text-blue-700', bg: 'bg-blue-50'
            },
          ].map(stat => (
            <Card key={stat.label} className={`${stat.bg} border-0`}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="coupon-status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="redeemed">Redeemed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">{coupons.length} record{coupons.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
            ) : coupons.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-slate-500 font-medium">No coupons found</p>
                <p className="text-xs text-slate-400 mt-1">Coupons are auto-generated when partners sign up</p>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="coupons-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {['Partner Name', 'Email', 'Assigned Code', 'Submitted Code', 'Status', 'Assigned Date', 'Submitted Date', 'Action'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {coupons.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50 transition-colors" data-testid={`coupon-row-${c.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{c.partner_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{c.partner_email}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-amber-50 text-amber-800 px-2 py-1 rounded border border-amber-200">{c.coupon_code}</span>
                      </td>
                      <td className="px-4 py-3">
                        {c.submitted_code ? (
                          <span className={`font-mono text-xs px-2 py-1 rounded border ${c.submitted_code === c.coupon_code ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                            {c.submitted_code}
                            {c.submitted_code !== c.coupon_code && <span className="ml-1 text-red-500">(MISMATCH)</span>}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(c.assigned_at)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{c.submitted_at ? fmtDate(c.submitted_at) : '—'}</td>
                      <td className="px-4 py-3">
                        {c.status === 'submitted' ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-3"
                              onClick={() => { setActionDialog({ coupon: c, type: 'approve' }); setActionNotes(''); }}
                              data-testid={`approve-coupon-${c.id}`}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7 px-3"
                              onClick={() => { setActionDialog({ coupon: c, type: 'reject' }); setActionNotes(''); }}
                              data-testid={`reject-coupon-${c.id}`}
                            >
                              <XCircle className="h-3 w-3 mr-1" /> Reject
                            </Button>
                          </div>
                        ) : c.status === 'redeemed' ? (
                          <span className="text-xs text-green-600 font-medium">✓ Credited ₹{(c.credit_amount ?? 5100).toLocaleString()}</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Approve / Reject Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={o => { if (!o) { setActionDialog(null); setActionNotes(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === 'approve' ? 'Approve & Credit ₹5,100' : 'Reject Coupon'}
            </DialogTitle>
          </DialogHeader>
          {actionDialog && (
            <div className="space-y-4 pt-1">
              <div className={`p-3 rounded border ${actionDialog.type === 'approve' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <p className="text-sm font-medium">{actionDialog.coupon.partner_name}</p>
                <p className="text-xs text-slate-500">{actionDialog.coupon.partner_email}</p>
                <p className="font-mono text-xs mt-1 text-amber-800">{actionDialog.coupon.coupon_code}</p>
              </div>
              {actionDialog.type === 'approve' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-800">₹5,100 will be <strong>immediately</strong> credited to this partner's wallet.</p>
                </div>
              )}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
                <textarea
                  className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Admin notes..."
                  value={actionNotes}
                  onChange={e => setActionNotes(e.target.value)}
                  data-testid="coupon-action-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionDialog(null); setActionNotes(''); }}>Cancel</Button>
            <Button
              className={actionDialog?.type === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              disabled={submitting}
              onClick={handleAction}
              data-testid={`confirm-coupon-${actionDialog?.type}`}
            >
              {submitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : null}
              {actionDialog?.type === 'approve' ? 'Approve & Credit' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Custom Coupon Modal */}
      <Dialog open={sendModal} onOpenChange={o => { if (!o) setSendModal(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-blue-500" /> Send Custom Coupon
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Partner Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                placeholder="partner@example.com"
                value={sendForm.partner_email}
                onChange={e => setSendForm(f => ({ ...f, partner_email: e.target.value }))}
                data-testid="send-coupon-email"
              />
              <p className="text-xs text-slate-400">Must be a registered Synergy Partner email</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Coupon Code <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. ABCD-1234"
                  value={sendForm.custom_code}
                  onChange={e => setSendForm(f => ({ ...f, custom_code: e.target.value.toUpperCase() }))}
                  className="flex-1 font-mono tracking-wider"
                  data-testid="send-coupon-code"
                />
                <Button type="button" variant="outline" size="sm" onClick={autoGenerateCode} data-testid="auto-generate-code-btn">
                  <Shuffle className="h-4 w-4 mr-1" /> Auto
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Select Offer <span className="text-red-500">*</span></Label>
              <Select value={sendForm.offer_id} onValueChange={v => setSendForm(f => ({ ...f, offer_id: v }))}>
                <SelectTrigger data-testid="send-coupon-offer-select">
                  <SelectValue placeholder="Choose an offer..." />
                </SelectTrigger>
                <SelectContent>
                  {activeOffers.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name} {o.wallet_credit_amount ? `— ₹${o.wallet_credit_amount.toLocaleString()}` : ''}
                    </SelectItem>
                  ))}
                  {activeOffers.length === 0 && (
                    <SelectItem value="__none__" disabled>No active offers found</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Email template of selected offer will be used</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModal(false)}>Cancel</Button>
            <Button
              onClick={handleSendCustomCoupon}
              disabled={sendSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-send-custom-coupon"
            >
              {sendSubmitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Send className="h-4 w-4 mr-1.5" />}
              {sendSubmitting ? 'Sending...' : 'Send Coupon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
